import React from 'react';

/**
 * Per-site anatomical illustrations for the 7 Jackson-Pollock skinfold
 * measurement points. Rendered inline inside the caliper knowledge base
 * article via the `[[illustration:<key>]]` marker (see articleRenderer).
 *
 * Each figure highlights:
 *   • a simplified body silhouette showing the relevant region,
 *   • a red "pinch" oval marking the exact measurement spot,
 *   • an arrow indicating the direction of the skinfold (vertical / diagonal).
 *
 * Kept as inline SVG so the article stays a single HTTP request and works
 * offline, dark/light mode, and print.
 */

const SKIN = '#f1f5f9';
const STROKE = '#334155';
const FOLD = '#dc2626';
const FOLD_LIGHT = '#fecaca';
const ARROW = '#0f172a';

// Shared arrow marker
const ArrowDefs = () => (
  <defs>
    <marker id="cs-arrow" viewBox="0 0 10 10" refX="8" refY="5"
            markerWidth="6" markerHeight="6" orient="auto-start-reverse">
      <path d="M 0 0 L 10 5 L 0 10 z" fill={ARROW} />
    </marker>
  </defs>
);

// A red skin-fold pinch marker.
const PinchMark = ({ cx, cy, angle = 0, w = 30, h = 10 }) => (
  <g transform={`translate(${cx} ${cy}) rotate(${angle})`}>
    <ellipse rx={w / 2} ry={h / 2} fill={FOLD_LIGHT} stroke={FOLD} strokeWidth="1.6" />
    <line x1={-w / 2} y1="0" x2={w / 2} y2="0" stroke={FOLD} strokeWidth="1.6" />
  </g>
);

// -------------------------------------------------------------------------
// The male upper-torso base used by the chest / midaxillary / abdomen /
// suprailiac / subscapular sites. Points are aligned to this canvas.
// -------------------------------------------------------------------------
const TorsoFront = () => (
  <g fill={SKIN} stroke={STROKE} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round">
    {/* neck */}
    <path d="M 112 10 L 112 24 L 148 24 L 148 10" />
    {/* torso */}
    <path
      d="M 90 30 Q 78 34 76 48
         L 68 92 Q 66 104 72 112
         L 84 152 Q 88 162 92 176
         L 100 214 Q 104 220 112 220
         L 148 220 Q 156 220 160 214
         L 168 176 Q 172 162 176 152
         L 188 112 Q 194 104 192 92
         L 184 48 Q 182 34 170 30
         Q 150 24 130 24 Q 110 24 90 30 Z"
    />
    {/* arm — left */}
    <path d="M 76 48 Q 60 54 54 74 L 46 128 Q 44 142 48 156" fill="none" />
    {/* arm — right */}
    <path d="M 184 48 Q 200 54 206 74 L 214 128 Q 216 142 212 156" fill="none" />
    {/* subtle chest / midline references */}
    <path d="M 130 40 L 130 214" stroke={STROKE} strokeWidth="0.6" strokeDasharray="2 3" opacity="0.55" fill="none" />
  </g>
);

// Rear torso for the subscapular site
const TorsoBack = () => (
  <g fill={SKIN} stroke={STROKE} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round">
    <path d="M 112 10 L 112 24 L 148 24 L 148 10" />
    <path
      d="M 90 30 Q 78 34 76 48
         L 68 92 Q 66 104 72 112
         L 84 152 Q 88 162 92 176
         L 100 214 Q 104 220 112 220
         L 148 220 Q 156 220 160 214
         L 168 176 Q 172 162 176 152
         L 188 112 Q 194 104 192 92
         L 184 48 Q 182 34 170 30
         Q 150 24 130 24 Q 110 24 90 30 Z"
    />
    <path d="M 76 48 Q 60 54 54 74 L 46 128 Q 44 142 48 156" fill="none" />
    <path d="M 184 48 Q 200 54 206 74 L 214 128 Q 216 142 212 156" fill="none" />
    {/* scapula outlines */}
    <path d="M 92 62 Q 108 92 118 118" fill="none" stroke={STROKE} strokeWidth="0.9" opacity="0.7" />
    <path d="M 168 62 Q 152 92 142 118" fill="none" stroke={STROKE} strokeWidth="0.9" opacity="0.7" />
    <path d="M 130 40 L 130 214" stroke={STROKE} strokeWidth="0.6" strokeDasharray="2 3" opacity="0.55" fill="none" />
  </g>
);

// Rear-view upper arm for the tricep
const UpperArmBack = () => (
  <g fill={SKIN} stroke={STROKE} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round">
    {/* shoulder */}
    <path d="M 60 20 Q 90 12 130 18 Q 170 24 200 34" fill="none" />
    {/* upper arm silhouette */}
    <path
      d="M 90 24
         Q 78 40 78 70
         L 82 168
         Q 84 190 96 210
         L 156 208
         Q 168 190 170 168
         L 172 70
         Q 172 40 160 24 Z"
    />
    {/* elbow line */}
    <line x1="82" y1="180" x2="170" y2="178" stroke={STROKE} strokeWidth="0.9" opacity="0.6" />
    {/* mid-arm reference line */}
    <line x1="82" y1="115" x2="170" y2="115" stroke={STROKE} strokeWidth="0.6" strokeDasharray="2 3" opacity="0.6" />
  </g>
);

// Front thigh
const Thigh = () => (
  <g fill={SKIN} stroke={STROKE} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round">
    {/* hip line */}
    <path d="M 40 18 Q 90 8 130 12 Q 170 16 220 20" fill="none" />
    {/* thigh silhouette */}
    <path
      d="M 70 20
         Q 58 60 62 118
         L 74 210
         Q 78 226 92 226
         L 168 226
         Q 182 226 186 210
         L 198 118
         Q 202 60 190 20 Z"
    />
    {/* knee suggestion */}
    <line x1="76" y1="220" x2="184" y2="220" stroke={STROKE} strokeWidth="0.9" opacity="0.6" />
    {/* mid-thigh reference */}
    <line x1="74" y1="125" x2="186" y2="125" stroke={STROKE} strokeWidth="0.6" strokeDasharray="2 3" opacity="0.6" />
  </g>
);

// -------------------------------------------------------------------------
// The map of illustrations. Every entry returns an <svg> ready to render.
// -------------------------------------------------------------------------
const FIGS = {
  // 1 — Mellkas (férfi): hónalj és mellbimbó közötti középpont, ferde redő
  chest: {
    caption: '1. Mellkas — hónalj és mellbimbó közötti középpont, ~45°-os ferde redő.',
    render: (
      <svg viewBox="0 0 260 240" xmlns="http://www.w3.org/2000/svg"
           role="img" aria-label="Mellkas mérési pont"
           className="w-full h-full">
        <ArrowDefs />
        <TorsoFront />
        {/* fold: diagonal ~45deg between armpit and nipple */}
        <PinchMark cx={99} cy={70} angle={-40} w={36} h={11} />
        {/* label callout */}
        <line x1={99} y1={70} x2={40} y2={40} stroke={ARROW} strokeWidth="1" markerEnd="url(#cs-arrow)" />
        <text x={20} y={30} fontSize="12" fontWeight="700" fill={ARROW}
              fontFamily="Inter, sans-serif">Mellkas</text>
        <text x={20} y={45} fontSize="10" fill={STROKE}
              fontFamily="Inter, sans-serif">~45° ferde</text>
      </svg>
    ),
  },

  // 2 — Midaxilláris (hónaljvonal, xiphoid magasságban), függőleges redő
  midaxillary: {
    caption: '2. Hónaljvonal (midaxilláris) — az oldalsó hónaljvonalban, szegycsont vég (xiphoid) magasságában, függőleges redő.',
    render: (
      <svg viewBox="0 0 260 240" xmlns="http://www.w3.org/2000/svg"
           role="img" aria-label="Midaxilláris mérési pont"
           className="w-full h-full">
        <ArrowDefs />
        <TorsoFront />
        {/* fold: vertical, on the mid-axillary line at xiphoid level */}
        <PinchMark cx={72} cy={118} angle={90} w={30} h={10} />
        <line x1={72} y1={118} x2={30} y2={140} stroke={ARROW} strokeWidth="1" markerEnd="url(#cs-arrow)" />
        <text x={10} y={158} fontSize="12" fontWeight="700" fill={ARROW}
              fontFamily="Inter, sans-serif">Hónaljvonal</text>
        <text x={10} y={172} fontSize="10" fill={STROKE}
              fontFamily="Inter, sans-serif">függőleges</text>
      </svg>
    ),
  },

  // 3 — Tricepsz: hátsó felkar középvonalán, függőleges redő
  triceps: {
    caption: '3. Tricepsz — a hátsó felkar középvonalán, a váll és a könyök közötti pontosan félúton, függőleges redő.',
    render: (
      <svg viewBox="0 0 260 240" xmlns="http://www.w3.org/2000/svg"
           role="img" aria-label="Tricepsz mérési pont"
           className="w-full h-full">
        <ArrowDefs />
        <UpperArmBack />
        {/* fold: vertical, mid-back of upper arm, halfway between shoulder and elbow */}
        <PinchMark cx={126} cy={115} angle={90} w={34} h={11} />
        <line x1={126} y1={115} x2={210} y2={90} stroke={ARROW} strokeWidth="1" markerEnd="url(#cs-arrow)" />
        <text x={188} y={80} fontSize="12" fontWeight="700" fill={ARROW}
              fontFamily="Inter, sans-serif">Tricepsz</text>
        <text x={188} y={94} fontSize="10" fill={STROKE}
              fontFamily="Inter, sans-serif">függőleges</text>
      </svg>
    ),
  },

  // 4 — Subscapularis (lapocka alatt), ~45°-os ferde redő
  subscapular: {
    caption: '4. Lapocka alatt (subscapularis) — 1-2 cm-rel a lapocka alsó csúcsa alatt, ~45°-os ferde redő.',
    render: (
      <svg viewBox="0 0 260 240" xmlns="http://www.w3.org/2000/svg"
           role="img" aria-label="Subscapularis mérési pont"
           className="w-full h-full">
        <ArrowDefs />
        <TorsoBack />
        {/* fold: diagonal ~45deg below the right scapula tip */}
        <PinchMark cx={148} cy={126} angle={45} w={34} h={11} />
        <line x1={148} y1={126} x2={228} y2={104} stroke={ARROW} strokeWidth="1" markerEnd="url(#cs-arrow)" />
        <text x={188} y={92} fontSize="12" fontWeight="700" fill={ARROW}
              fontFamily="Inter, sans-serif">Lapocka</text>
        <text x={188} y={106} fontSize="10" fill={STROKE}
              fontFamily="Inter, sans-serif">~45° ferde</text>
      </svg>
    ),
  },

  // 5 — Has (abdominal): köldöktől 2 cm-re oldalra, függőleges redő
  abdomen: {
    caption: '5. Has (abdominális) — a köldöktől ~2 cm-re oldalra (jobbra), függőleges redő.',
    render: (
      <svg viewBox="0 0 260 240" xmlns="http://www.w3.org/2000/svg"
           role="img" aria-label="Has mérési pont"
           className="w-full h-full">
        <ArrowDefs />
        <TorsoFront />
        {/* navel */}
        <circle cx={130} cy={170} r={2.6} fill={STROKE} />
        {/* fold: vertical, 2cm lateral to navel */}
        <PinchMark cx={148} cy={170} angle={90} w={30} h={10} />
        <line x1={148} y1={170} x2={228} y2={188} stroke={ARROW} strokeWidth="1" markerEnd="url(#cs-arrow)" />
        <text x={188} y={210} fontSize="12" fontWeight="700" fill={ARROW}
              fontFamily="Inter, sans-serif">Has</text>
        <text x={188} y={224} fontSize="10" fill={STROKE}
              fontFamily="Inter, sans-serif">függőleges</text>
      </svg>
    ),
  },

  // 6 — Suprailiakális (csípőlapát felett), ferde redő természetes vonalban
  suprailiac: {
    caption: '6. Csípőlapát (suprailiakális) — közvetlenül a csípőcsont felső pereme fölött, ferde redő a bőr természetes vonalában.',
    render: (
      <svg viewBox="0 0 260 240" xmlns="http://www.w3.org/2000/svg"
           role="img" aria-label="Suprailiakális mérési pont"
           className="w-full h-full">
        <ArrowDefs />
        <TorsoFront />
        {/* iliac crest reference */}
        <path d="M 76 202 Q 130 210 184 202" fill="none" stroke={STROKE} strokeWidth="0.9" strokeDasharray="2 3" opacity="0.7" />
        {/* fold: ~30deg above iliac crest on the right */}
        <PinchMark cx={166} cy={192} angle={-30} w={34} h={11} />
        <line x1={166} y1={192} x2={228} y2={168} stroke={ARROW} strokeWidth="1" markerEnd="url(#cs-arrow)" />
        <text x={190} y={158} fontSize="12" fontWeight="700" fill={ARROW}
              fontFamily="Inter, sans-serif">Csípőlapát</text>
        <text x={190} y={172} fontSize="10" fill={STROKE}
              fontFamily="Inter, sans-serif">~30° ferde</text>
      </svg>
    ),
  },

  // 7 — Comb (mid-thigh), függőleges redő
  thigh: {
    caption: '7. Comb — a csípő és a térd közötti pontos félúton, a comb elülső középvonalán, függőleges redő.',
    render: (
      <svg viewBox="0 0 260 240" xmlns="http://www.w3.org/2000/svg"
           role="img" aria-label="Comb mérési pont"
           className="w-full h-full">
        <ArrowDefs />
        <Thigh />
        {/* fold: vertical on midline of thigh */}
        <PinchMark cx={130} cy={125} angle={90} w={34} h={11} />
        <line x1={130} y1={125} x2={228} y2={100} stroke={ARROW} strokeWidth="1" markerEnd="url(#cs-arrow)" />
        <text x={198} y={90} fontSize="12" fontWeight="700" fill={ARROW}
              fontFamily="Inter, sans-serif">Comb</text>
        <text x={198} y={104} fontSize="10" fill={STROKE}
              fontFamily="Inter, sans-serif">függőleges</text>
      </svg>
    ),
  },
};

export const CALIPER_SITE_KEYS = Object.keys(FIGS);

const CaliperSiteFigure = ({ site }) => {
  const fig = FIGS[site];
  if (!fig) return null;
  return (
    <figure
      data-testid={`caliper-site-fig-${site}`}
      className="my-6 rounded-xl border border-gray-200 bg-gradient-to-br from-slate-50 to-white p-4 md:p-6 shadow-sm"
    >
      <div className="mx-auto max-w-sm aspect-[13/12]">
        {fig.render}
      </div>
      <figcaption className="mt-3 text-sm text-gray-600 leading-relaxed text-center">
        {fig.caption}
      </figcaption>
    </figure>
  );
};

export default CaliperSiteFigure;
